/**
 * API Utility Module
 * 
 * Wrapper around fetch for WordPress REST API calls.
 * Automatically includes nonce for authentication.
 * 
 * @package PresszoneComments
 */

const API = {
    /**
     * Base API URL from localized script data.
     */
    get baseUrl() {
        return window.presszoneCommentsAdmin?.apiUrl || '/wp-json/comments-press-zone/v1';
    },

    /**
     * WP Nonce for authentication.
     */
    get nonce() {
        return window.presszoneCommentsAdmin?.nonce || '';
    },

    /**
     * Make a GET request.
     * @param {string} path - API endpoint path (e.g., '/nodes')
     * @returns {Promise<any>}
     */
    async get(path) {
        const response = await fetch(this.baseUrl + path, {
            method: 'GET',
            headers: {
                'X-WP-Nonce': this.nonce,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(error.message || window.presszoneCommentsAdmin?.i18n?.apiRequestFailed || 'API request failed');
        }

        return response.json();
    },

    /**
     * Make a POST request.
     * @param {string} path - API endpoint path
     * @param {object} data - Request body data
     * @returns {Promise<any>}
     */
    async post(path, data = {}) {
        const response = await fetch(this.baseUrl + path, {
            method: 'POST',
            headers: {
                'X-WP-Nonce': this.nonce,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(error.message || window.presszoneCommentsAdmin?.i18n?.apiRequestFailed || 'API request failed');
        }

        return response.json();
    },

    /**
     * Make a DELETE request.
     * @param {string} path - API endpoint path
     * @param {object} data - Optional query parameters
     * @returns {Promise<any>}
     */
    async delete(path, data = {}) {
        let url = this.baseUrl + path;
        
        // Append query params if data is provided
        if (Object.keys(data).length > 0) {
            const params = new URLSearchParams(data).toString();
            url += (url.includes('?') ? '&' : '?') + params;
        }

        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'X-WP-Nonce': this.nonce,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(error.message || window.presszoneCommentsAdmin?.i18n?.apiRequestFailed || 'API request failed');
        }

        return response.json();
    },
};

export default API;
